import dayjs from "dayjs";

import type { AnnotationsDetailData, AnnotationsModelModelsAnnotationType } from "@similarweb/pro-api-client";
import type { IAnnotation } from "@similarweb/pro-logic-components";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";

import type { IAccountUser } from "app/sharing/SharingService";
import { translate } from "app/filters/i18nFilter";

export const dateToUTC = (dateString: string): number => {
    const date = dateString.split("-");
    return Date.UTC(parseInt(date[0]), parseInt(date[1]) - 1, parseInt(date[2]));
};

export type PreconfiguredUpdateType = Exclude<AnnotationsModelModelsAnnotationType, "user_defined">;

export interface IDeserializeAnnotationOptions {
    userList?: Record<IAccountUser["Id"], IAccountUser>;
}

const AUTHORS_ALIAS_KEYS: Record<PreconfiguredUpdateType, string> = {
    google_update: "annotations.authors.google",
    similarweb_update: "annotations.authors.similarweb",
} as const;

/**
 * Deserializes annotation data from API response into chart annotation format.
 *
 * This function handles multiple annotation types:
 * - Legacy updates: Uses pre-configured descriptions and links from legacyUpdates map (optional)
 * - Standard Google/SimilarWeb updates: Uses provided text and meta information
 * - User-defined annotations: Uses provided text and resolves author from userList
 *
 * @param annotationData - Raw annotation data from API response
 * @param options - Optional configuration including userList and legacyUpdates map
 * @returns Formatted annotation object ready for chart display
 */
export const deserializeAnnotation = (
    { type, id = 0, key, text, meta, userId }: AnnotationsDetailData,
    options?: IDeserializeAnnotationOptions,
): IAnnotation => {
    const { userList } = options || {};
    const isUserAnnotations = type === "user_defined";
    const pointX = dateToUTC(key);
    const content: IAnnotation["content"] = {
        createdLabel: dayjs(pointX).format(shortMonthDayAndYear),
        text: isUserAnnotations ? text : translate(text),
        author: AUTHORS_ALIAS_KEYS?.[type]
            ? translate(AUTHORS_ALIAS_KEYS?.[type] as string)
            : (userList?.[userId]?.User ?? userId.toString()),
        ...(meta?.link
            ? {
                  link: meta.link,
                  linkLabel: meta.text || translate("global.labels.learnMore"),
              }
            : {}),
    };

    return {
        pointX,
        type,
        id,
        permissions: {
            canDelete: type === "user_defined",
        },
        content,
    };
};
